import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { VEHICLES_LIST, VEHICLES_LIST_FILTERED, VEHICLE_DELETE } from '../../graphql/vehicles';
import { BUSINESS_UNIT_LIST } from '../../graphql/employees';
import Pagination from '../../components/Pagination';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton, 
  CircularProgress, 
  TextField, 
  MenuItem, 
  Box, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  Typography,
  Chip,
  Avatar,
  Card,
  CardContent,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  DirectionsCar,
  Warning,
  CheckCircle,
  Schedule
} from '@mui/icons-material';

const statusOptions = ['All', 'Active', 'Inactive', 'Retired', 'Maintenance'];

// Add mock defect reports for demonstration
const mockDefectReports = [
  { id: '1', vehicleId: 'veh1', description: 'Brake light not working', status: 'Open' },
  { id: '2', vehicleId: 'veh2', description: 'Oil leak under engine', status: 'Open' },
  { id: '3', vehicleId: 'veh3', description: 'Tire pressure warning', status: 'Resolved' },
  { id: '4', vehicleId: 'veh1', description: 'Wiper blade torn', status: 'Open' },
];

const getStatusColor = (status) => {
  switch (status) {
    case 'Active': return { bg: '#10b981', color: 'white' };
    case 'Inactive': return { bg: '#6b7280', color: 'white' };
    case 'Retired': return { bg: '#ef4444', color: 'white' };
    case 'Maintenance': return { bg: '#f59e0b', color: 'white' };
    default: return { bg: '#e5e7eb', color: '#374151' };
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'Active': return <CheckCircle sx={{ fontSize: 16 }} />;
    case 'Inactive': return <Schedule sx={{ fontSize: 16 }} />;
    case 'Retired': return <Warning sx={{ fontSize: 16 }} />;
    case 'Maintenance': return <Warning sx={{ fontSize: 16 }} />;
    default: return <DirectionsCar sx={{ fontSize: 16 }} />;
  }
};

const VehicleList = ({ onEdit }) => {
  const { data: businessUnitsData } = useQuery(BUSINESS_UNIT_LIST);
  const [deleteVehicle] = useMutation(VEHICLE_DELETE);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, vehicle: null });
  const [searchParams, setSearchParams] = useSearchParams();

  // Get pagination and filter state from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const itemsPerPage = parseInt(searchParams.get('limit')) || 10;
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || 'All';
  const year = searchParams.get('year') || 'All';
  const businessUnit = searchParams.get('businessUnit') || 'All';

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce search input
  useEffect(() => {
    if (search !== debouncedSearch) {
      setIsSearching(true);
    }
    
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  // Update URL parameters
  const updateURLParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'All' && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Handle filter changes
  const handleSearchChange = (value) => {
    updateURLParams({ search: value, page: 1 });
  };

  const handleStatusChange = (value) => {
    updateURLParams({ status: value, page: 1 });
  };

  const handleYearChange = (value) => {
    updateURLParams({ year: value, page: 1 });
  };

  const handleBusinessUnitChange = (value) => {
    updateURLParams({ businessUnit: value, page: 1 });
  };

  const handlePageChange = (page) => {
    updateURLParams({ page });
  };

  const handleItemsPerPageChange = (limit) => {
    updateURLParams({ limit, page: 1 });
  };

  // Build filter object for GraphQL query
  const buildFilter = () => {
    const filter = {};
    
    if (status !== 'All') {
      filter.status = { equals: status };
    }
    
    if (year !== 'All') {
      filter.year = { equals: parseInt(year) };
    }
    
    if (businessUnit !== 'All') {
      filter.businessUnit = { name: { equals: businessUnit } };
    }
    
    if (debouncedSearch.trim()) {
      filter.OR = [
        { make: { contains: debouncedSearch } },
        { model: { contains: debouncedSearch } },
        { licensePlate: { contains: debouncedSearch } },
        { unitNumber: { contains: debouncedSearch } }
      ];
    }
    
    return Object.keys(filter).length > 0 ? filter : null;
  };

  const { data, loading, error, refetch } = useQuery(
    VEHICLES_LIST_FILTERED,
    {
      variables: { 
        filter: buildFilter(),
        first: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage
      },
      fetchPolicy: 'network-only'
    }
  );

  const handleDeleteClick = (vehicle) => {
    setDeleteDialog({ open: true, vehicle });
  };

  const handleDeleteConfirm = async () => {
    await deleteVehicle({ variables: { id: deleteDialog.vehicle.id } });
    refetch();
    setDeleteDialog({ open: false, vehicle: null });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, vehicle: null });
  };

  // Get all vehicles for filter options (without filters)
  const { data: allVehiclesData } = useQuery(VEHICLES_LIST);

  const yearOptions = ['All', ...Array.from(new Set((allVehiclesData?.vehiclesList?.items || []).map(v => v.year)))];
  const businessUnitOptions = ['All', ...(businessUnitsData?.businessUnitsList?.items?.map(bu => bu.name) || [])];

  // Use the filtered data directly from the server
  const filteredVehicles = data?.vehiclesList?.items || [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Error loading vehicles.</Typography>
      </Card>
    );
  }

  return (
    <Box>
      {/* Search and Filters */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Search & Filters
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search Vehicles"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Search by make, model, license plate, or unit number"
              size="small"
              sx={{ minWidth: 300, flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: isSearching && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Status"
              value={status}
              onChange={e => handleStatusChange(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              {statusOptions.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Year"
              value={year}
              onChange={e => handleYearChange(e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
            >
              {yearOptions.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Business Unit"
              value={businessUnit}
              onChange={e => handleBusinessUnitChange(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              {businessUnitOptions.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {isSearching ? 'Searching...' : `Found ${data?.vehiclesList?.count || 0} vehicles`}
            </Typography>
            {isSearching && <CircularProgress size={16} />}
          </Box>
        </CardContent>
      </Card>

      {/* Vehicle Table */}
      <Card sx={{ 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Vehicle</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Details</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Business Unit</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Insurance</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Records</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Defects</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVehicles.map((vehicle) => {
                const defectCount = mockDefectReports.filter(r => r.vehicleId === vehicle.id).length;
                const statusConfig = getStatusColor(vehicle.status);
                
                return (
                  <TableRow 
                    key={vehicle.id} 
                    hover 
                    onClick={() => onEdit(vehicle)} 
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'rgba(99, 102, 241, 0.04)',
                        transform: 'scale(1.01)',
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: '#6366f1', 
                            width: 40, 
                            height: 40, 
                            mr: 2,
                            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                          }}
                        >
                          <DirectionsCar sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {vehicle.make} {vehicle.model}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Unit #{vehicle.unitNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {vehicle.year} • {vehicle.licensePlate}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          VIN: {vehicle.vIN}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {vehicle.businessUnit?.name || 'Not assigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(vehicle.status)}
                        label={vehicle.status}
                        size="small"
                        sx={{
                          bgcolor: statusConfig.bg,
                          color: statusConfig.color,
                          fontWeight: 600,
                          '& .MuiChip-icon': {
                            color: statusConfig.color
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {vehicle.insurancePolicy?.provider || 'Not specified'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip 
                          label={`M: ${vehicle.maintenanceRecords?.items?.length || 0}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                        <Chip 
                          label={`I: ${vehicle.inspections?.items?.length || 0}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                        <Chip 
                          label={`W: ${vehicle.workOrders?.items?.length || 0}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={defectCount}
                        size="small"
                        color={defectCount > 0 ? 'error' : 'default'}
                        variant={defectCount > 0 ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit Vehicle">
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(vehicle);
                            }} 
                            size="small" 
                            sx={{
                              color: '#6366f1',
                              '&:hover': {
                                bgcolor: 'rgba(99, 102, 241, 0.1)'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Vehicle">
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(vehicle);
                            }} 
                            size="small" 
                            sx={{
                              color: '#ef4444',
                              '&:hover': {
                                bgcolor: 'rgba(239, 68, 68, 0.1)'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil((data?.vehiclesList?.count || 0) / itemsPerPage)}
          totalItems={data?.vehiclesList?.count || 0}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Delete Vehicle
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography>
            Are you sure you want to delete this vehicle? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VehicleList; 